/**
 * Global loading / error / 403 conventions — 13-ui-screens.md §4.6.
 *
 * A route that is pending renders a `Skeleton` shaped like its real layout,
 * NEVER a centred spinner. A failed query renders an antd `Result` inside the
 * body band with the server's `message` as the subtitle and a `Try again`
 * action wired to `refetch()`.
 */
import { Button, Result, Skeleton } from 'antd';
import type { ReactNode } from 'react';
import { ApiError, NetworkError } from '@/api/client';
import { NavAnchor } from '@/components/primitives/NavAnchor';

export interface QueryBoundaryProps {
  /** TanStack Query v5: `isPending`, NOT `isLoading` (which now means
   *  `isPending && isFetching` and so flickers during a background refetch). */
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  children: ReactNode;
  /** Rows to draw while pending, matching the real layout. */
  skeletonRows?: number;
}

export function QueryBoundary({
  isPending,
  error,
  onRetry,
  children,
  skeletonRows = 6,
}: QueryBoundaryProps) {
  if (error) {
    if (error instanceof ApiError && error.status === 403) {
      return <ForbiddenResult />;
    }
    const subtitle =
      error instanceof ApiError || error instanceof NetworkError
        ? error.message
        : 'An unexpected error occurred.';
    return (
      <Result
        status="error"
        title="Could not load this screen."
        subTitle={subtitle}
        extra={
          <Button type="primary" onClick={onRetry}>
            Try again
          </Button>
        }
      />
    );
  }
  if (isPending) return <RouteSkeleton rows={skeletonRows} />;
  return <>{children}</>;
}

export function RouteSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div style={{ padding: '16px 20px 40px' }}>
      <Skeleton active paragraph={{ rows }} title />
    </div>
  );
}

export function ForbiddenResult({
  roleLabel,
  landingRoute = '/',
  landingLabel = 'your home screen',
}: {
  roleLabel?: string;
  landingRoute?: string;
  landingLabel?: string;
}) {
  return (
    <Result
      status="403"
      title="You do not have permission to open this screen."
      subTitle={
        roleLabel
          ? `Your role is ${roleLabel}. Ask an Administrator if this is wrong.`
          : 'Ask an Administrator if this is wrong.'
      }
      extra={
        <NavAnchor to={landingRoute}>
          <Button type="primary">{`Back to ${landingLabel}`}</Button>
        </NavAnchor>
      }
    />
  );
}
